import { CustomValidator, UiSchema } from '@rjsf/utils';
import { CreatePromptRequest, Project } from 'api/model';
import { getProjectsOptions } from 'api/workflows-steps/formSchema';
import ListSelectionWidget from 'components/form/ListSelectionWidget';
import { JSONSchema7 } from 'json-schema';
import ResizableTextarea from 'components/form/ResizableTextarea';
import FolderPathWidget from 'components/form/FolderPathWidget';
import { validateFolderPath } from 'utils/folderPath';

const required = ['prompt_name', 'type', 'template'];

function getFormSchema(
  projects?: Project[],
  projectId?: string,
  fieldsInputs?: string[],
  templateHint?: string,
): JSONSchema7 {
  const projectsOptions = getProjectsOptions(projects);
  const properties = fieldsInputs?.reduce((a, v) => ({ ...a, [v]: { type: 'string' } }), {});

  return {
    type: 'object',
    properties: {
      prompt_name: {
        title: 'Name',
        type: 'string',
      },
      description: {
        title: 'Description',
        type: 'string',
      },
      type: {
        title: 'Type',
        type: 'string',
        enum: ['chat'],
        default: 'chat',
      },
      project_id: {
        title: 'Project',
        type: ['string', 'null'],
        description: 'The project this prompt belongs to. If not set, the prompt will be global.',
        default: projectId ?? null,
        anyOf: [{ const: null }, ...projectsOptions],
      },
      folder_path: {
        title: 'Folder',
        type: ['string', 'null'],
        description: 'Organize the prompt into a folder path, e.g. assessment/java.',
      },
      template: {
        title: 'Template',
        type: 'string',
        description: templateHint,
      },
      inputs: {
        type: 'array',
        title: 'Input fields',
        items: {
          type: 'string',
          title: 'field name',
        },
      },
      examples: {
        type: 'array',
        title: 'Examples',
        items: {
          type: 'object',
          properties: {
            example_inputs: {
              type: 'object',
              properties: properties,
            },
            output: {
              type: 'string',
            },
          },
        },
      },
    },
    required,
  };
}

function getUiSchema(fieldInputs?: string[], requiredInputs?: string[], additionalInputsAllowed?: boolean): UiSchema {
  const exampleInputUiSchema = {
    'ui:widget': ResizableTextarea,
    'ui:options': {
      rows: 3,
    },
  };
  const exampleInputsWidgets = fieldInputs?.reduce((a, v) => ({ ...a, [v]: exampleInputUiSchema }), {});

  return {
    template: {
      'ui:widget': ResizableTextarea,
      'ui:options': {
        rows: 4,
        style: {
          resize: 'vertical',
        },
      },
    },
    inputs: {
      'ui:requiredItems': requiredInputs ?? [],
      'ui:disabled': !additionalInputsAllowed,
    },
    examples: {
      items: {
        example_inputs: {
          'ui:options': {
            label: false,
          },
          ...exampleInputsWidgets,
        },
        output: {
          'ui:widget': ResizableTextarea,
          'ui:options': {
            rows: 5,
          },
        },
      },
    },
    project_id: {
      'ui:widget': ListSelectionWidget,
      'ui:options': {
        autocomplete: true,
      },
    },
    folder_path: {
      'ui:widget': FolderPathWidget,
    },
  };
}

const validator: CustomValidator<CreatePromptRequest> = (formData, errors) => {
  const folderError = validateFolderPath(formData?.folder_path);
  if (folderError) {
    errors.folder_path?.addError(folderError);
  }

  if (formData?.inputs) {
    const seenInputs = new Set<string>();
    for (const input of formData.inputs) {
      if (!input || input.trim() === '') {
        errors.inputs?.addError('Input fields cannot be empty');
      } else if (formData.template && !formData.template.includes(`{${input}}`)) {
        errors.inputs?.addError(`Input field "${input}" must be included in the template as {${input}}`);
      }

      if (seenInputs.has(input)) {
        errors.inputs?.addError(`Input field "${input}" is duplicated`);
      } else {
        seenInputs.add(input);
      }
    }

    const templateInputs = formData.template.match(/(?<!\{)\{([\w ]*)}(?!})/g);
    if (templateInputs) {
      for (const input of templateInputs) {
        const inputName = input.slice(1, -1);
        if (!seenInputs.has(inputName)) {
          errors.template?.addError(`Input field "${inputName}" is used in the template but not defined in inputs`);
        }
      }
    }
  }
  return errors;
};

export { getFormSchema, getUiSchema, validator };
